import React, { useState, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useTranslation } from "react-i18next";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Collapse from "@mui/material/Collapse";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActionsMui from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Backdrop from "@mui/material/Backdrop";
import Chip from "@mui/material/Chip";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import {
  GfFlyerItemFigmaJob,
  GfFlyerItemFigmaJobGetManyResponseDto,
  GfImage,
  MissingPositionDto,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../../../../utils/api";
import TemplateDataCard from "../../../../components/TemplateDataCard";
import { GfFigmaTemplateData } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../locales/messages";
import { useDownloadManager } from "../../../../hooks/useDownloadManager";
import { makeStoreDisplayName } from "../../../../utils/dtoUtil/makeStoreDisplayName";
import { Stack } from "@mui/material";
import ImagePreviewZoomModal from "../../../../components/ImagePreviewZoomModal";

export type GfGeneratedFlyerImagesModalProps = {
  flyerItemListId: string;
  open: boolean;
  callbackCloseOpen: () => void;
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 4,
};

const buttonColor = "#ff8038";

const GfGeneratedFlyerImagesModal: React.FC<
  GfGeneratedFlyerImagesModalProps
> = ({ flyerItemListId, open, callbackCloseOpen }) => {
  const { t } = useTranslation();
  const PAGE_SIZE = 10;
  const [flyerSets, setFlyerSets] = useState<GfFlyerItemFigmaJob[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | undefined>();
  const [previewImages, setPreviewImages] = useState<GfImage[]>([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState<number>(0);
  const [activeTemplate, setActiveTemplate] = useState<
    GfFigmaTemplateData | undefined
  >();
  const [downloading, setDownloading] = useState<boolean>(false);
  const [downloadMessage, setDownloadMessage] = useState<string>("");

  const downloadManager = useDownloadManager({
    onSuccess: (filename) => {
      console.log("Download successful:", filename);
      setDownloadMessage("Download completed successfully");
      setTimeout(() => {
        setDownloading(false);
        setDownloadMessage("");
      }, 1500);
    },
    onError: (error) => {
      console.error("Download error:", error);
      const userMessage = {
        NETWORK:
          "Network error occurred. Please check your connection and try again.",
        CORS: "Unable to download due to security restrictions. Trying alternative method...",
        FILE_SIZE:
          "File is too large to download. Please try individual downloads.",
        TIMEOUT: "Download timed out. Please try again.",
        UNKNOWN: "An unexpected error occurred during download.",
      }[error.type];
      setDownloadMessage(userMessage);
      setTimeout(() => {
        setDownloading(false);
        setDownloadMessage("");
      }, 3000);
    },
    onProgress: (message) => {
      setDownloadMessage(message);
    },
  });

  useEffect(() => {
    setDownloading(downloadManager.isDownloading);
    if (downloadManager.downloadMessage) {
      setDownloadMessage(downloadManager.downloadMessage);
    }
  }, [downloadManager.isDownloading, downloadManager.downloadMessage]);

  // If none (or invalid), append/replace with .jpg (default) while normalizing spaces.
  const ensureImageExtension = (
    fileName?: string,
    defaultExt = "jpg",
  ): string | undefined => {
    if (!fileName) return fileName;
    const sanitized = fileName.trim().replace(/\s+/g, "_");
    if (!sanitized) return sanitized;

    const allowed = ["jpg", "jpeg", "png", "webp", "gif", "avif"];
    const extMatch = sanitized.match(/\.([a-z0-9]+)$/i);

    if (extMatch) {
      const currentExt = extMatch[1].toLowerCase();
      if (allowed.includes(currentExt)) {
        // Normalize .jpeg to .jpg
        if (currentExt === "jpeg") {
          return sanitized.replace(/\.jpeg$/i, ".jpg");
        }
        return sanitized;
      }
      // Invalid extension -> replace with default
      return sanitized.replace(/\.[a-z0-9]+$/i, `.${defaultExt}`);
    }

    return `${sanitized}.${defaultExt}`;
  };
  const handleDownload = async (url: string, preferredName?: string) => {
    setDownloading(true);
    await downloadManager.downloadFile({
      url,
      preferredName,
      maxRetries: 3,
      timeout: 30000,
    });
  };

  const handleDownloadAllZip = async (flyerSet: GfFlyerItemFigmaJob) => {
    if (!flyerSet.generatedFlyerImages || !flyerSet.generatedFlyerImages.length)
      return;

    const sortedImages = [...flyerSet.generatedFlyerImages].sort(
      (a, b) => a.index - b.index,
    );
    const imageUrls = sortedImages.map((imgObj) => ({
      url: imgObj.gfImage.src,
      filename: `${ensureImageExtension(imgObj.gfImage.fileName)}`,
    }));

    const zipFilename = flyerSet?.flyerItemList?.stores[0]?.merchant
      ? `${makeStoreDisplayName(flyerSet?.flyerItemList.stores[0])}-${
          flyerSet.generatedFlyerImages.length
        }-flyers.zip`
      : `flyer_images_${imageUrls.length}items.zip`;

    setDownloading(true);
    await downloadManager.downloadMultipleFiles(imageUrls, zipFilename);
  };

  const handleDownloadAllImages = async (flyerSet: GfFlyerItemFigmaJob) => {
    if (!flyerSet.generatedFlyerImages || !flyerSet.generatedFlyerImages.length)
      return;

    const sortedImages = [...flyerSet.generatedFlyerImages].sort(
      (a, b) => a.index - b.index,
    );
    const imageUrls = sortedImages.map((imgObj) => ({
      url: imgObj.gfImage.src,
      filename: `${ensureImageExtension(imgObj.gfImage.fileName)}`,
    }));

    setDownloading(true);
    await downloadManager.downloadMultipleFiles(imageUrls);
  };

  const fetchFlyerSets = async (pageNumber: number): Promise<void> => {
    setLoading(true);
    try {
      const data: GfFlyerItemFigmaJobGetManyResponseDto =
        await adminApiService.getManyBaseGfFlyerItemFigmaJobAdminControllerGfFlyerItemFigmaJob(
          undefined,
          undefined,
          [`flyerItemList.id||eq||${flyerItemListId}`],
          undefined,
          ["updatedAt,DESC"],
          [
            "figmaTemplateData",
            "figmaTemplateData.image",
            "figmaTemplateData.category",
            "figmaTemplateData.figmaTemplateTag",
          ],
          PAGE_SIZE,
          undefined,
          pageNumber,
          undefined,
        );

      const items = data.data || [];

      if (items.length > 0) {
        setFlyerSets((prev) => [...prev, ...items]);
        const newExpanded: { [key: string]: boolean } = {};
        items.forEach((item) => {
          newExpanded[item.id] = true;
        });
        setExpanded((prev) => ({ ...prev, ...newExpanded }));
      }

      setHasMore(items.length === PAGE_SIZE);
    } catch (error) {
      console.error("Error fetching flyer sets:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setFlyerSets([]);
      setPage(1);
      setHasMore(true);
      fetchFlyerSets(1);
    }
  }, [open, flyerItemListId]);

  const loadMoreFlyerSets = (): void => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    fetchFlyerSets(nextPage);
    setPage(nextPage);
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleOpenTemplate = (tpl?: GfFigmaTemplateData) => {
    if (!tpl) return;
    setActiveTemplate(tpl);
    setTemplateModalOpen(true);
  };
  const handleCloseTemplate = () => {
    setTemplateModalOpen(false);
    setActiveTemplate(undefined);
    setPreviewSrc(undefined);
    setTemplatePreviewOpen(false);
  };
  const handlePreviewImage = (
    src?: string,
    allImages?: GfImage[],
    index?: number,
  ) => {
    if (!src) return;
    setPreviewSrc(src);

    if (allImages && allImages.length > 0) {
      setPreviewImages(allImages);
      setCurrentPreviewIndex(index !== undefined ? index : 0);
    } else {
      setPreviewImages([]);
      setCurrentPreviewIndex(0);
    }

    setTemplatePreviewOpen(true);
  };
  const handleClosePreview = () => {
    setTemplatePreviewOpen(false);
    setPreviewSrc(undefined);
    setPreviewImages([]);
    setCurrentPreviewIndex(0);
  };

  const handleNavigatePreview = (index: number) => {
    setCurrentPreviewIndex(index);
    if (previewImages[index]) {
      setPreviewSrc(previewImages[index].src);
    }
  };

  const formatDate = (dateString: string | Date) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    return date.toLocaleString("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatTimeOnly = (dateString: string | Date) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    return date.toLocaleString("en-US", {
      timeZone: "America/New_York",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const noDataAvailable = !loading && flyerSets.length === 0;

  return (
    <>
      <Modal open={open} onClose={callbackCloseOpen}>
        <Box sx={modalStyle}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              textAlign: "center",
              mb: 3,
              fontWeight: "bold",
              color: buttonColor,
            }}
          >
            {t(messages.generatedFlyersTitle())}
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: "center", mb: 2, color: "text.secondary" }}
          >
            {t(messages.flyerSetsDescription())}
          </Typography>

          {noDataAvailable ? (
            <Typography
              variant="body1"
              sx={{ textAlign: "center", color: "text.secondary", mt: 4 }}
            >
              {t(messages.noFlyersAvailable())}
            </Typography>
          ) : (
            <div
              className="scroll-container-generated-flyers"
              id="scrollableDiv"
            >
              <InfiniteScroll
                dataLength={flyerSets.length}
                next={loadMoreFlyerSets}
                hasMore={hasMore}
                loader={
                  <Box display="flex" justifyContent="center" my={2}>
                    <CircularProgress />
                  </Box>
                }
                scrollableTarget="scrollableDiv"
                style={{ overflow: "visible" }}
              >
                <List disablePadding>
                  {flyerSets.map((flyerSet) => {
                    const creationDate = formatDate(flyerSet.createdAt);
                    const completedDate = formatDate(flyerSet.updatedAt);
                    const validityStart = formatDate(
                      flyerSet.flyerItemList.validStartDate,
                    );
                    const validityEnd = formatDate(
                      flyerSet.flyerItemList.validEndDate,
                    );
                    const imageCount =
                      flyerSet.generatedFlyerImages?.length || 0;

                    const sortedImages = flyerSet.generatedFlyerImages
                      ? [...flyerSet.generatedFlyerImages].sort(
                          (a, b) => a.index - b.index,
                        )
                      : [];

                    return (
                      <React.Fragment key={flyerSet.id}>
                        <ListItem disableGutters sx={{ mt: 2 }}>
                          <Card
                            variant="outlined"
                            sx={{ width: "100%", borderRadius: 2 }}
                          >
                            <CardContent>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Box>
                                  <Stack
                                    direction={"row"}
                                    alignItems={"center"}
                                    columnGap={1}
                                  >
                                    {/* <Typography
                                      variant="h6"
                                      sx={{ fontWeight: "600" }}
                                    >
                                      {t(messages.flyerSetLabel())} {index + 1}
                                    </Typography> */}
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.75rem",
                                        color: "text.secondary",
                                        display: "block",
                                        mt: 0.5,
                                      }}
                                    >
                                      {formatTimeOnly(flyerSet.createdAt)}
                                    </Typography>
                                  </Stack>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    <strong>{t(messages.statusLabel())}</strong>{" "}
                                    <Typography
                                      component="span"
                                      variant="body2"
                                      sx={{
                                        fontWeight: "600",
                                        color:
                                          flyerSet.status ===
                                          GfFlyerItemFigmaJob.StatusEnum
                                            .Completed
                                            ? "success.main"
                                            : flyerSet.status ===
                                                GfFlyerItemFigmaJob.StatusEnum
                                                  .Error
                                              ? "error.main"
                                              : "warning.main",
                                      }}
                                    >
                                      {flyerSet.status ===
                                        GfFlyerItemFigmaJob.StatusEnum
                                          .Error && (
                                        <ErrorIcon
                                          sx={{
                                            fontSize: 16,
                                            mr: 0.5,
                                            verticalAlign: "middle",
                                          }}
                                        />
                                      )}
                                      {flyerSet.status}
                                    </Typography>
                                  </Typography>
                                </Box>
                                <Box
                                  display="flex"
                                  flexWrap={"wrap"}
                                  justifyContent="end"
                                  alignItems="center"
                                >
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() =>
                                      handleDownloadAllZip(flyerSet)
                                    }
                                    startIcon={<DownloadIcon />}
                                    disabled={
                                      flyerSet.status ===
                                      GfFlyerItemFigmaJob.StatusEnum.Error
                                    }
                                    sx={{
                                      backgroundColor:
                                        flyerSet.status ===
                                        GfFlyerItemFigmaJob.StatusEnum.Error
                                          ? "grey.400"
                                          : buttonColor,
                                      "&:hover": {
                                        backgroundColor:
                                          flyerSet.status ===
                                          GfFlyerItemFigmaJob.StatusEnum.Error
                                            ? "grey.400"
                                            : buttonColor,
                                      },
                                      fontSize: "0.75rem",
                                      padding: "4px 8px",
                                      minWidth: "80px",
                                    }}
                                  >
                                    {t(messages.downloadAllZipButton())}
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                      handleDownloadAllImages(flyerSet)
                                    }
                                    startIcon={<DownloadIcon />}
                                    disabled={
                                      flyerSet.status ===
                                      GfFlyerItemFigmaJob.StatusEnum.Error
                                    }
                                    sx={{
                                      ml: 1,
                                      borderColor: buttonColor,
                                      color: buttonColor,
                                      fontSize: "0.75rem",
                                      padding: "4px 8px",
                                      minWidth: "80px",
                                      "&:hover": {
                                        borderColor: buttonColor,
                                        backgroundColor: `${buttonColor}10`,
                                      },
                                    }}
                                  >
                                    {t(messages.downloadAllImagesButton())}
                                  </Button>
                                  {flyerSet.figmaTemplateData && (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<VisibilityIcon />}
                                      onClick={() =>
                                        handleOpenTemplate(
                                          flyerSet.figmaTemplateData,
                                        )
                                      }
                                      sx={{
                                        ml: 1,
                                        borderColor: buttonColor,
                                        color: buttonColor,
                                        fontSize: "0.75rem",
                                        padding: "4px 8px",
                                        minWidth: "80px",
                                        "&:hover": {
                                          borderColor: buttonColor,
                                          backgroundColor: `${buttonColor}10`,
                                        },
                                      }}
                                    >
                                      {t(messages.viewTemplateButton())}
                                    </Button>
                                  )}
                                  <IconButton
                                    onClick={() => toggleExpand(flyerSet.id)}
                                  >
                                    {expanded[flyerSet.id] ? (
                                      <ExpandLess />
                                    ) : (
                                      <ExpandMore />
                                    )}
                                  </IconButton>
                                </Box>
                              </Box>

                              <Divider sx={{ my: 2 }} />

                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{t(messages.createdOnLabel())}</strong>{" "}
                                {creationDate}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>
                                  {flyerSet.status ===
                                  GfFlyerItemFigmaJob.StatusEnum.Error
                                    ? t(messages.failedOnLabel())
                                    : t(messages.completedOnLabel())}
                                </strong>{" "}
                                {completedDate}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>
                                  {t(messages.totalFlyerItemsLabel())}
                                </strong>{" "}
                                {flyerSet.totalFlyerItemCount}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{t(messages.validityLabel())}</strong>{" "}
                                {validityStart} - {validityEnd}
                              </Typography>
                              {flyerSet?.adminUser && (
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                  <strong>{t(messages.Triggered_By())}:</strong>{" "}
                                  {`${flyerSet.adminUser.firstName || ""} ${
                                    flyerSet.adminUser.lastName || ""
                                  }`.trim()}
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>
                                  {t(messages.generatedImagesLabel())}
                                </strong>{" "}
                                {imageCount}
                              </Typography>

                              {flyerSet.figmaTemplateData && (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    p: 1.5,
                                    backgroundColor: "#f9f9f9",
                                    borderRadius: 1,
                                    border: "1px solid #e0e0e0",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: "600",
                                      mb: 1,
                                      color: buttonColor,
                                      display: "block",
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    📄 {t(messages.templateDetailsTitle())}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      mb: 0.5,
                                      fontSize: "0.875rem",
                                    }}
                                    noWrap
                                    title={flyerSet.figmaTemplateData.name}
                                  >
                                    {flyerSet.figmaTemplateData.name}
                                  </Typography>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                      mb: 1,
                                    }}
                                  >
                                    {flyerSet.figmaTemplateData
                                      .figmaTemplateTag && (
                                      <Chip
                                        size="small"
                                        label={
                                          flyerSet.figmaTemplateData
                                            .figmaTemplateTag.name
                                        }
                                        sx={{
                                          bgcolor: `${buttonColor}1A`,
                                          color: buttonColor,
                                          fontWeight: 600,
                                          fontSize: 10,
                                          height: 20,
                                          "& .MuiChip-label": {
                                            px: 0.8,
                                            py: 0,
                                          },
                                        }}
                                      />
                                    )}
                                    {flyerSet.figmaTemplateData.type && (
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={flyerSet.figmaTemplateData.type
                                          .toString()
                                          .toUpperCase()}
                                        sx={{
                                          borderColor:
                                            flyerSet.figmaTemplateData.type
                                              .toString()
                                              .toLowerCase() === "specific"
                                              ? "#E65100"
                                              : buttonColor,
                                          bgcolor:
                                            flyerSet.figmaTemplateData.type
                                              .toString()
                                              .toLowerCase() === "specific"
                                              ? "#E651000F"
                                              : `${buttonColor}0D`,
                                          color:
                                            flyerSet.figmaTemplateData.type
                                              .toString()
                                              .toLowerCase() === "specific"
                                              ? "#BF360C"
                                              : buttonColor,
                                          fontWeight: 700,
                                          letterSpacing: 0.4,
                                          fontSize: 9.5,
                                          height: 22,
                                          "& .MuiChip-label": {
                                            px: 0.8,
                                            py: 0,
                                          },
                                        }}
                                      />
                                    )}
                                    <Chip
                                      size="small"
                                      label={`${
                                        flyerSet.figmaTemplateData
                                          .numberOfPages || 0
                                      } ${
                                        (flyerSet.figmaTemplateData
                                          .numberOfPages || 0) > 1
                                          ? "pages"
                                          : "page"
                                      }`}
                                      sx={{
                                        bgcolor: "#f5f5f5",
                                        color: "text.secondary",
                                        fontWeight: 500,
                                        fontSize: 10,
                                        height: 20,
                                        "& .MuiChip-label": { px: 0.8, py: 0 },
                                      }}
                                    />
                                  </Box>

                                  {flyerSet.figmaTemplateData.category &&
                                    flyerSet.figmaTemplateData.category.length >
                                      0 && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.5,
                                        }}
                                      >
                                        {flyerSet.figmaTemplateData.category.map(
                                          (cat) => (
                                            <Chip
                                              size="small"
                                              key={cat.id}
                                              label={cat.name}
                                              sx={{
                                                bgcolor: `${buttonColor}14`,
                                                color: "#424242",
                                                fontWeight: 500,
                                                fontSize: 10,
                                                height: 20,
                                                "& .MuiChip-label": {
                                                  px: 0.8,
                                                  py: 0,
                                                },
                                              }}
                                            />
                                          ),
                                        )}
                                      </Box>
                                    )}
                                </Box>
                              )}

                              {flyerSet.status ===
                              GfFlyerItemFigmaJob.StatusEnum.Error ? (
                                <Box
                                  sx={{
                                    mt: 2,
                                    p: 2,
                                    backgroundColor: "#ffebee",
                                    borderRadius: 1,
                                    border: "1px solid #f44336",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#d32f2f",
                                      fontWeight: "600",
                                      mb: 1,
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <ErrorIcon sx={{ fontSize: 18, mr: 1 }} />
                                    {t(messages.errorDetailsLabel())}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#666",
                                      fontFamily: "monospace",
                                      backgroundColor: "#fff",
                                      p: 1,
                                      borderRadius: 0.5,
                                      border: "1px solid #ddd",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {flyerSet.errorDetails
                                      ? typeof flyerSet.errorDetails ===
                                        "string"
                                        ? flyerSet.errorDetails
                                        : JSON.stringify(
                                            flyerSet.errorDetails,
                                            null,
                                            2,
                                          )
                                      : t(messages.noErrorDetailsMessage())}
                                  </Typography>
                                  {flyerSet.retryCount &&
                                  flyerSet.retryCount > 0 ? (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#666",
                                        fontStyle: "italic",
                                        mt: 1,
                                        display: "block",
                                      }}
                                    >
                                      {t(messages.retryAttemptsLabel(), {
                                        count: flyerSet.retryCount,
                                      })}
                                    </Typography>
                                  ) : (
                                    <></>
                                  )}
                                </Box>
                              ) : (
                                <></>
                              )}

                              {flyerSet.missingPositions &&
                                flyerSet.missingPositions.length > 0 && (
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong style={{ color: "#ff8038" }}>
                                      ⚠️ {t(messages.layoutNotice())}:
                                    </strong>{" "}
                                    <span style={{ color: "#d32f2f" }}>
                                      {flyerSet.missingPositions
                                        .map(
                                          (missing: MissingPositionDto) =>
                                            `${missing.name} (${t(
                                              messages.missingPositionItemCount(),
                                              { count: missing.count },
                                            )})`,
                                        )
                                        .join(", ")}{" "}
                                      {t(messages.layoutExceedsConfiguration())}
                                    </span>
                                  </Typography>
                                )}
                            </CardContent>

                            <Collapse
                              in={expanded[flyerSet.id]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <CardContent>
                                {imageCount > 0 ? (
                                  <Grid container spacing={2}>
                                    {sortedImages.map((imgObj, idx) => {
                                      const allImages = sortedImages.map(
                                        (val) => val.gfImage,
                                      );
                                      return (
                                        <Grid
                                          item
                                          xs={12}
                                          sm={6}
                                          md={3}
                                          key={imgObj.id}
                                        >
                                          <Box
                                            sx={{
                                              position: "relative",
                                              border: "1px solid",
                                              borderColor: "divider",
                                              borderRadius: 2,
                                              overflow: "hidden",
                                              display: "flex",
                                              flexDirection: "column",
                                              height: "100%",
                                              backgroundColor: "#fafafa",
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                position: "relative",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                height: 300,
                                                overflow: "hidden",
                                                backgroundColor: "#f5f5f5",
                                                "&:hover": {
                                                  "& .preview-img": {
                                                    transform:
                                                      "scale(1.05) translateZ(0)",
                                                  },
                                                  "& .preview-overlay": {
                                                    opacity: 1,
                                                  },
                                                },
                                              }}
                                            >
                                              <Box
                                                component="img"
                                                className="preview-img"
                                                src={imgObj.gfImage.src}
                                                alt={
                                                  imgObj.gfImage.alt ||
                                                  `${t(
                                                    messages.flyerItemLabel(),
                                                  )} ${idx + 1}`
                                                }
                                                sx={{
                                                  width: "100%",
                                                  height: "100%",
                                                  objectFit: "contain",
                                                  display: "block",
                                                  cursor: "pointer",
                                                  transition:
                                                    "transform .35s cubic-bezier(0.2, 0, 0.2, 1)",
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                aria-label={t(
                                                  messages.Preview_Image(),
                                                )}
                                                onClick={() =>
                                                  handlePreviewImage(
                                                    imgObj.gfImage.src,
                                                    allImages,
                                                    idx,
                                                  )
                                                }
                                                onKeyDown={(event) => {
                                                  if (
                                                    event.key === "Enter" ||
                                                    event.key === " "
                                                  ) {
                                                    event.preventDefault();
                                                    handlePreviewImage(
                                                      imgObj.gfImage.src,
                                                      allImages,
                                                      idx,
                                                    );
                                                  }
                                                }}
                                              />
                                              <Box
                                                className="preview-overlay"
                                                sx={{
                                                  position: "absolute",
                                                  inset: 0,
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  pointerEvents: "none",
                                                  background:
                                                    "linear-gradient(180deg, rgba(0,0,0,0.0) 20%, rgba(0,0,0,0.45) 100%)",
                                                  color: "#fff",
                                                  opacity: 0,
                                                  transition:
                                                    "opacity .3s ease",
                                                  backdropFilter: "blur(2px)",
                                                }}
                                              >
                                                <Typography
                                                  variant="button"
                                                  sx={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    px: 1.4,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    bgcolor:
                                                      "rgba(255,255,255,0.25)",
                                                    backdropFilter: "blur(4px)",
                                                    textTransform: "uppercase",
                                                  }}
                                                >
                                                  {t(messages.Preview())}
                                                </Typography>
                                              </Box>
                                            </Box>

                                            <Box p={1} flexGrow={1}>
                                              <Typography
                                                variant="body2"
                                                sx={{ fontWeight: "500" }}
                                              >
                                                {`${t(
                                                  messages.flyerItemLabel(),
                                                )} ${idx + 1}`}
                                              </Typography>
                                            </Box>
                                            <CardActions
                                              sx={{
                                                justifyContent: "flex-end",
                                              }}
                                            >
                                              <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<DownloadIcon />}
                                                onClick={() => {
                                                  void handleDownload(
                                                    imgObj.gfImage.src,
                                                    ensureImageExtension(
                                                      imgObj.gfImage.fileName ||
                                                        `${(
                                                          imgObj.gfImage.alt ||
                                                          `flyer_item_${idx + 1}`
                                                        ).padStart(3, "0")}`,
                                                    ),
                                                  );
                                                }}
                                                sx={{
                                                  backgroundColor: buttonColor,
                                                  "&:hover": {
                                                    backgroundColor:
                                                      buttonColor,
                                                  },
                                                  fontSize: "0.75rem",
                                                  padding: "4px 8px",
                                                  minWidth: "80px",
                                                }}
                                              >
                                                {t(messages.downloadButton())}
                                              </Button>
                                            </CardActions>
                                          </Box>
                                        </Grid>
                                      );
                                    })}
                                  </Grid>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ textAlign: "center", p: 2 }}
                                  >
                                    {t(messages.noImagesAvailable())}
                                  </Typography>
                                )}

                                {flyerSet.status !==
                                  GfFlyerItemFigmaJob.StatusEnum.Error &&
                                  flyerSet.missingPositions &&
                                  flyerSet.missingPositions.length > 0 && (
                                    <>
                                      <Divider sx={{ my: 3 }} />
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          mb: 2,
                                          color: "#ff8038",
                                          fontWeight: "600",
                                        }}
                                      >
                                        ⚠️{" "}
                                        {t(
                                          messages.layoutConfigurationNoticeTitle(),
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          mb: 2,
                                          color: "text.secondary",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {t(
                                          messages.layoutConfigurationNoticeDescription(),
                                        )}
                                      </Typography>
                                      <Grid container spacing={2}>
                                        {flyerSet.missingPositions.map(
                                          (
                                            missing: MissingPositionDto,
                                            index: number,
                                          ) => (
                                            <Grid
                                              item
                                              xs={12}
                                              sm={6}
                                              md={4}
                                              key={`${missing.id}-${index}`}
                                            >
                                              <Box
                                                sx={{
                                                  border: "1px solid #ff8038",
                                                  borderRadius: 2,
                                                  p: 2,
                                                  backgroundColor: "#fff3e0",
                                                }}
                                              >
                                                <Typography
                                                  variant="subtitle2"
                                                  sx={{
                                                    fontWeight: "600",
                                                    color: "#e65100",
                                                    mb: 1,
                                                  }}
                                                >
                                                  {missing.name}
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  sx={{ mb: 0.5 }}
                                                >
                                                  <strong>
                                                    🏷️{" "}
                                                    {t(
                                                      messages.itemsAffected(),
                                                    )}
                                                    :
                                                  </strong>{" "}
                                                  {t(
                                                    messages.missingPositionItemCount(),
                                                    { count: missing.count },
                                                  )}
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: "#d32f2f",
                                                    fontStyle: "italic",
                                                    display: "block",
                                                    mt: 1,
                                                  }}
                                                >
                                                  {t(
                                                    messages.categoryExceedsCapacity(),
                                                  )}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                          ),
                                        )}
                                      </Grid>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          mt: 2,
                                          p: 2,
                                          backgroundColor: "#e3f2fd",
                                          borderRadius: 1,
                                          color: "#1565c0",
                                          border: "1px solid #90caf9",
                                        }}
                                      >
                                        <strong>
                                          📞 {t(messages.needAssistance())}
                                        </strong>{" "}
                                        {t(messages.layoutAssistanceMessage())}
                                      </Typography>
                                    </>
                                  )}
                              </CardContent>
                            </Collapse>
                          </Card>
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              </InfiniteScroll>
            </div>
          )}

          {loading && !flyerSets.length && (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          )}
          <Dialog
            open={templateModalOpen}
            onClose={handleCloseTemplate}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <DialogTitle sx={{ pr: 6 }}>
              {t(messages.templateDetailsTitle())}
              <IconButton
                onClick={handleCloseTemplate}
                sx={{ position: "absolute", right: 12, top: 8 }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ bgcolor: "#fafafa" }}>
              {activeTemplate && (
                <TemplateDataCard
                  template={activeTemplate}
                  isSelected={false}
                  selectable={false}
                  onPreview={handlePreviewImage}
                />
              )}
            </DialogContent>
            <DialogActionsMui>
              <Button onClick={handleCloseTemplate}>
                {t(messages.Close())}
              </Button>
            </DialogActionsMui>
          </Dialog>
          <ImagePreviewZoomModal
            open={templatePreviewOpen}
            onClose={handleClosePreview}
            imageSrc={previewSrc}
            imageAlt="Template Preview"
            onDownload={() => {
              if (previewSrc) {
                const currentImg = previewImages[currentPreviewIndex];
                const fileName = currentImg?.fileName || undefined;
                handleDownload(previewSrc, ensureImageExtension(fileName));
              }
            }}
            downloadDisabled={!previewSrc}
            title={t(messages.imagePreviewTitle())}
            images={previewImages}
            currentIndex={currentPreviewIndex}
            onNavigate={handleNavigatePreview}
          />
        </Box>
      </Modal>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={downloading}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          maxWidth="400px"
        >
          <CircularProgress color="inherit" />
          {downloadMessage && (
            <Typography
              variant="body2"
              sx={{
                color: "#fff",
                textAlign: "center",
                backgroundColor: "rgba(0,0,0,0.3)",
                padding: "8px 16px",
                borderRadius: "4px",
                backdropFilter: "blur(4px)",
              }}
            >
              {downloadMessage}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.7)",
              textAlign: "center",
              maxWidth: "300px",
            }}
          >
            {t(messages.downloadWarningMessage())}
          </Typography>
        </Box>
      </Backdrop>
    </>
  );
};

export default GfGeneratedFlyerImagesModal;
